import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, LogOut, Heart, Users, Flag, Eye, EyeOff, Trash2, 
  ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, Clock,
  BarChart3, TrendingUp
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [warriors, setWarriors] = useState([]);
  const [prayerPage, setPrayerPage] = useState(1);
  const [prayerPages, setPrayerPages] = useState(1);
  const [prayerFilter, setPrayerFilter] = useState('all');
  const [warriorPage, setWarriorPage] = useState(1);
  const [warriorPages, setWarriorPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [prayerToDelete, setPrayerToDelete] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchData();
  }, [activeTab, prayerPage, prayerFilter, warriorPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || activeTab === 'prayers') {
        const statsRes = await axios.get(`${API}/admin/stats`, { withCredentials: true });
        setStats(statsRes.data);
      }
      
      if (activeTab === 'prayers') {
        const prayersRes = await axios.get(
          `${API}/admin/prayers?page=${prayerPage}&status=${prayerFilter}`,
          { withCredentials: true }
        );
        setPrayers(prayersRes.data.prayers);
        setPrayerPages(prayersRes.data.pages);
      }
      
      if (activeTab === 'warriors') {
        const warriorsRes = await axios.get(
          `${API}/admin/warriors?page=${warriorPage}`,
          { withCredentials: true }
        );
        setWarriors(warriorsRes.data.warriors);
        setWarriorPages(warriorsRes.data.pages);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async (prayerId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/prayers/${prayerId}?is_public=${!currentStatus}`,
        {},
        { withCredentials: true }
      );
      setPrayers(prev => prev.map(p => 
        p.id === prayerId ? { ...p, is_public: !currentStatus } : p
      ));
      showMessage('success', 'Prayer visibility updated');
    } catch (error) {
      showMessage('error', 'Failed to update prayer');
    }
  };

  const toggleFlag = async (prayerId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/prayers/${prayerId}?flagged=${!currentStatus}`,
        {},
        { withCredentials: true }
      );
      setPrayers(prev => prev.map(p => 
        p.id === prayerId ? { ...p, flagged: !currentStatus } : p
      ));
      showMessage('success', currentStatus ? 'Flag removed' : 'Prayer flagged for review');
    } catch (error) {
      showMessage('error', 'Failed to update prayer');
    }
  };

  const deletePrayer = async () => {
    if (!prayerToDelete) return;
    try {
      await axios.delete(`${API}/admin/prayers/${prayerToDelete}`, { withCredentials: true });
      setPrayers(prev => prev.filter(p => p.id !== prayerToDelete));
      showMessage('success', 'Prayer deleted successfully');
      if (stats) {
        setStats(prev => ({ ...prev, total_prayers: prev.total_prayers - 1 }));
      }
    } catch (error) {
      showMessage('error', 'Failed to delete prayer');
    } finally {
      setDeleteDialogOpen(false);
      setPrayerToDelete(null);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5A6E4B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 border-b border-[#E6E2D6]/50">
        <div className="px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-heading text-2xl font-semibold text-[#2B2E26]" data-testid="admin-logo">
              Pray Together
            </Link>
            <span className="bg-[#5A6E4B] text-white text-xs px-2 py-1 rounded-full font-body">Admin</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="hidden md:block font-body text-[#5C6056]">{user?.name}</span>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-[#5C6056] hover:text-[#2B2E26] transition-colors font-body"
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] bg-white border-r border-[#E6E2D6] p-6 hidden md:block">
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-left transition-colors ${
                activeTab === 'overview' 
                  ? 'bg-[#5A6E4B] text-white' 
                  : 'text-[#5C6056] hover:bg-[#F4F1E9]'
              }`}
              data-testid="tab-overview"
            >
              <BarChart3 size={20} />
              Overview
            </button>
            <button
              onClick={() => { setActiveTab('prayers'); setPrayerPage(1); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-left transition-colors ${
                activeTab === 'prayers' 
                  ? 'bg-[#5A6E4B] text-white' 
                  : 'text-[#5C6056] hover:bg-[#F4F1E9]'
              }`}
              data-testid="tab-prayers"
            >
              <Heart size={20} />
              Prayer Requests
              {stats?.flagged_prayers > 0 && (
                <span className="ml-auto bg-[#C86A51] text-white text-xs px-2 py-0.5 rounded-full">
                  {stats.flagged_prayers}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('warriors'); setWarriorPage(1); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-left transition-colors ${
                activeTab === 'warriors' 
                  ? 'bg-[#5A6E4B] text-white' 
                  : 'text-[#5C6056] hover:bg-[#F4F1E9]'
              }`}
              data-testid="tab-warriors"
            >
              <Users size={20} />
              Prayer Warriors
            </button>
          </nav>
        </aside>

        {/* Mobile tabs */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E6E2D6] p-2 flex justify-around z-40">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg ${activeTab === 'overview' ? 'text-[#5A6E4B]' : 'text-[#8A8D86]'}`}
          >
            <BarChart3 size={20} />
            <span className="text-xs">Overview</span>
          </button>
          <button
            onClick={() => setActiveTab('prayers')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg relative ${activeTab === 'prayers' ? 'text-[#5A6E4B]' : 'text-[#8A8D86]'}`}
          >
            <Heart size={20} />
            <span className="text-xs">Prayers</span>
            {stats?.flagged_prayers > 0 && (
              <span className="absolute top-1 right-2 bg-[#C86A51] text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                {stats.flagged_prayers}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('warriors')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg ${activeTab === 'warriors' ? 'text-[#5A6E4B]' : 'text-[#8A8D86]'}`}
          >
            <Users size={20} />
            <span className="text-xs">Warriors</span>
          </button>
        </div>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8 pb-24 md:pb-8" data-testid="admin-dashboard">
          {/* Message */}
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                message.type === 'success' ? 'bg-[#5A6E4B]/10 text-[#5A6E4B]' : 'bg-red-50 text-red-600'
              }`}
              data-testid="admin-message"
            >
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
              {message.text}
            </motion.div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-heading text-3xl font-medium text-[#2B2E26] mb-8" data-testid="overview-title">
                Dashboard Overview
              </h1>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 text-[#5C6056] mb-2">
                    <Heart size={18} />
                    <span className="font-body text-sm">Total Prayers</span>
                  </div>
                  <p className="font-heading text-3xl font-semibold text-[#2B2E26]" data-testid="overview-total-prayers">
                    {stats.total_prayers}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 text-[#5C6056] mb-2">
                    <Eye size={18} />
                    <span className="font-body text-sm">Public</span>
                  </div>
                  <p className="font-heading text-3xl font-semibold text-[#2B2E26]">
                    {stats.public_prayers}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 text-[#5C6056] mb-2">
                    <Users size={18} />
                    <span className="font-body text-sm">Warriors</span>
                  </div>
                  <p className="font-heading text-3xl font-semibold text-[#2B2E26]" data-testid="overview-total-warriors">
                    {stats.total_warriors}
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <div className="flex items-center gap-2 text-[#C86A51] mb-2">
                    <Flag size={18} />
                    <span className="font-body text-sm">Flagged</span>
                  </div>
                  <p className="font-heading text-3xl font-semibold text-[#C86A51]" data-testid="overview-flagged">
                    {stats.flagged_prayers}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <h3 className="font-heading text-xl text-[#2B2E26] mb-4 flex items-center gap-2">
                    <TrendingUp size={20} className="text-[#5A6E4B]" />
                    Last 7 Days
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-[#5C6056]">New Prayers</span>
                      <span className="font-heading text-xl text-[#2B2E26]">{stats.recent_prayers_7d}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-[#5C6056]">Prayers Completed</span>
                      <span className="font-heading text-xl text-[#2B2E26]">{stats.recent_completions_7d}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#E6E2D6]">
                  <h3 className="font-heading text-xl text-[#2B2E26] mb-4 flex items-center gap-2">
                    <Clock size={20} className="text-[#5A6E4B]" />
                    Assignment Stats
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-[#5C6056]">Total Assignments</span>
                      <span className="font-heading text-xl text-[#2B2E26]">{stats.total_assignments}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-[#5C6056]">Completed</span>
                      <span className="font-heading text-xl text-[#5A6E4B]">{stats.completed_assignments}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Prayers Tab */}
          {activeTab === 'prayers' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h1 className="font-heading text-3xl font-medium text-[#2B2E26]" data-testid="prayers-title">
                  Prayer Requests
                </h1>
                
                <div className="flex gap-2">
                  {['all', 'public', 'private', 'flagged'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => { setPrayerFilter(filter); setPrayerPage(1); }}
                      className={`px-4 py-2 rounded-full text-sm font-body capitalize transition-colors ${
                        prayerFilter === filter
                          ? 'bg-[#5A6E4B] text-white'
                          : 'bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6]'
                      }`}
                      data-testid={`filter-${filter}`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5A6E4B]"></div>
                </div>
              ) : prayers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[#E6E2D6]">
                  <Heart size={48} className="mx-auto text-[#E6E2D6] mb-4" />
                  <p className="font-body text-[#5C6056]">No prayers found</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {prayers.map((prayer) => (
                      <div
                        key={prayer.id}
                        className={`bg-white p-6 rounded-2xl border ${prayer.flagged ? 'border-[#C86A51]' : 'border-[#E6E2D6]'}`}
                        data-testid="admin-prayer-card"
                      >
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-heading text-xl text-[#2B2E26]">{prayer.person_name}</h3>
                              {prayer.flagged && (
                                <span className="bg-[#C86A51]/10 text-[#C86A51] text-xs px-2 py-1 rounded-full font-body">
                                  Flagged
                                </span>
                              )}
                              {prayer.is_public ? (
                                <span className="bg-[#5A6E4B]/10 text-[#5A6E4B] text-xs px-2 py-1 rounded-full font-body">
                                  Public
                                </span>
                              ) : (
                                <span className="bg-[#F4F1E9] text-[#5C6056] text-xs px-2 py-1 rounded-full font-body">
                                  Private
                                </span>
                              )}
                            </div>
                            <p className="font-body text-[#5C6056] mb-3">{prayer.intention}</p>
                            <div className="flex flex-wrap gap-4 text-xs text-[#8A8D86] font-body">
                              {prayer.submitter_name && <span>From: {prayer.submitter_name}</span>}
                              <span>Prayed: {prayer.times_prayed} times</span>
                              <span>Assigned: {prayer.times_assigned} warriors</span>
                              <span>{new Date(prayer.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => togglePublic(prayer.id, prayer.is_public)}
                              className={`p-2 rounded-lg transition-colors ${
                                prayer.is_public 
                                  ? 'bg-[#5A6E4B]/10 text-[#5A6E4B] hover:bg-[#5A6E4B]/20' 
                                  : 'bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6]'
                              }`}
                              title={prayer.is_public ? 'Make private' : 'Make public'}
                              data-testid="toggle-public-btn"
                            >
                              {prayer.is_public ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                            <button
                              onClick={() => toggleFlag(prayer.id, prayer.flagged)}
                              className={`p-2 rounded-lg transition-colors ${
                                prayer.flagged 
                                  ? 'bg-[#C86A51]/10 text-[#C86A51] hover:bg-[#C86A51]/20' 
                                  : 'bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6]'
                              }`}
                              title={prayer.flagged ? 'Remove flag' : 'Flag for review'}
                              data-testid="toggle-flag-btn"
                            >
                              <Flag size={18} />
                            </button>
                            <button
                              onClick={() => { setPrayerToDelete(prayer.id); setDeleteDialogOpen(true); }}
                              className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                              title="Delete prayer"
                              data-testid="delete-prayer-btn"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {prayerPages > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                      <button
                        onClick={() => setPrayerPage(p => Math.max(1, p - 1))}
                        disabled={prayerPage === 1}
                        className="p-2 rounded-lg bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6] disabled:opacity-50"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="flex items-center px-4 font-body text-[#5C6056]">
                        Page {prayerPage} of {prayerPages}
                      </span>
                      <button
                        onClick={() => setPrayerPage(p => Math.min(prayerPages, p + 1))}
                        disabled={prayerPage === prayerPages}
                        className="p-2 rounded-lg bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6] disabled:opacity-50"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* Warriors Tab */}
          {activeTab === 'warriors' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="font-heading text-3xl font-medium text-[#2B2E26] mb-6" data-testid="warriors-title">
                Prayer Warriors
              </h1>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5A6E4B]"></div>
                </div>
              ) : warriors.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[#E6E2D6]">
                  <Users size={48} className="mx-auto text-[#E6E2D6] mb-4" />
                  <p className="font-body text-[#5C6056]">No warriors registered yet</p>
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-2xl border border-[#E6E2D6] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#F4F1E9]">
                          <tr>
                            <th className="text-left px-6 py-4 font-body font-medium text-[#5C6056]">Name</th>
                            <th className="text-left px-6 py-4 font-body font-medium text-[#5C6056]">Email</th>
                            <th className="text-left px-6 py-4 font-body font-medium text-[#5C6056]">Prayers Completed</th>
                            <th className="text-left px-6 py-4 font-body font-medium text-[#5C6056]">Joined</th>
                          </tr>
                        </thead>
                        <tbody>
                          {warriors.map((warrior) => (
                            <tr key={warrior.id} className="border-t border-[#E6E2D6]" data-testid="warrior-row">
                              <td className="px-6 py-4 font-body text-[#2B2E26]">{warrior.name}</td>
                              <td className="px-6 py-4 font-body text-[#5C6056]">{warrior.email}</td>
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center gap-1 font-body text-[#5A6E4B]">
                                  <Heart size={14} className="fill-current" />
                                  {warrior.prayers_completed}
                                </span>
                              </td>
                              <td className="px-6 py-4 font-body text-[#8A8D86]">
                                {new Date(warrior.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {warriorPages > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                      <button
                        onClick={() => setWarriorPage(p => Math.max(1, p - 1))}
                        disabled={warriorPage === 1}
                        className="p-2 rounded-lg bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6] disabled:opacity-50"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span className="flex items-center px-4 font-body text-[#5C6056]">
                        Page {warriorPage} of {warriorPages}
                      </span>
                      <button
                        onClick={() => setWarriorPage(p => Math.min(warriorPages, p + 1))}
                        disabled={warriorPage === warriorPages}
                        className="p-2 rounded-lg bg-[#F4F1E9] text-[#5C6056] hover:bg-[#E6E2D6] disabled:opacity-50"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </main>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl text-[#2B2E26]">Delete Prayer Request</AlertDialogTitle>
            <AlertDialogDescription className="font-body text-[#5C6056]">
              Are you sure you want to delete this prayer request? This will also remove all associated assignments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary py-2 px-4">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deletePrayer}
              className="bg-red-500 text-white px-4 py-2 rounded-full hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
